import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../components/AuthProvider';
import { BigScreenStage, type BigScreenBoardView } from '../components/scoreboard/BigScreenStage';
import { BigScreenSlideshow } from '../components/scoreboard/BigScreenSlideshow';
import { screenTheme } from '../lib/big-screen-analytics';
import { ApiError, apiFetch } from '../lib/api';
import { apiUrl } from '../lib/api-base';
import {
  BIG_SCREEN_DEFAULT_QUALIFIERS,
  BIG_SCREEN_POLL_MS,
  bigScreenColumns,
  bigScreenThemeOrDark,
  chennaiClock,
  qualifierCountsFor,
  type BigScreenTheme,
} from '../lib/big-screen';
import { loadBigScreenQueue } from '../lib/big-screen-queue';
import { RIFLE_TOURNAMENT_SLUG, scoringWebSocketUrl, type PublicLeaderboard } from '../lib/shooting';
import { ScoreboardNotFound } from './ScoreboardPage';
import '../styles/big-screen.css';

function useStageScale(): number {
  const [scale, setScale] = useState(1);
  useEffect(() => {
    const fit = () => setScale(Math.min(window.innerWidth / 1920, window.innerHeight / 1080) || 1);
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, []);
  return scale;
}

function boardSignature(board: PublicLeaderboard): string {
  return JSON.stringify({
    tournament: board.tournament,
    theme: board.bigScreen?.theme ?? null,
    storedTheme: board.bigScreen?.storedTheme,
    updatedAt: board.updatedAt,
    round: board.bigScreen?.round ?? 1,
    missing: board.bigScreen?.missingGender ?? null,
    qualifiers: board.bigScreen?.qualifiers ?? null,
    queue: board.queue ?? null,
    round2Queue: board.round2Queue ?? null,
    rows: board.rows,
    round1: board.round1 ?? null,
    round2: board.round2 ?? null,
  });
}

/**
 * /scoreboard/:slug/display — Round 1 on a landscape TV. The phone and laptop board stays on
 * /scoreboard/:slug and links here, so its paging and search are unchanged.
 */
export function BigScreenPage() {
  const { slug = RIFLE_TOURNAMENT_SLUG } = useParams();
  const [params] = useSearchParams();
  const { isAdmin: admin, isOrganizer } = useAuth();
  const isAdmin = admin || Boolean(isOrganizer && slug === RIFLE_TOURNAMENT_SLUG);
  const scale = useStageScale();
  const [board, setBoard] = useState<PublicLeaderboard | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [live, setLive] = useState(false);
  const [clock, setClock] = useState(() => chennaiClock());
  const [pendingTheme, setPendingTheme] = useState<BigScreenTheme | null>(null);
  const [themeError, setThemeError] = useState<string | null>(null);
  const [themeSaving, setThemeSaving] = useState(false);
  const holdEnd = params.get('frame') === 'end';

  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  useEffect(() => {
    const id = window.setInterval(() => setClock(chennaiClock()), 1000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    document.title = board?.tournament.title ? `${board.tournament.title} · Big screen` : 'Big screen · SMYM';
  }, [board?.tournament.title]);

  useEffect(() => {
    let socket: WebSocket | null = null;
    let pollTimer: number | null = null;
    let reconnectTimer: number | null = null;
    let attempt = 0;
    let stopped = false;
    let seen = '';
    let pulling = false;
    let socketRevision = 0;
    const controller = new AbortController();
    setBoard(null);
    setNotFound(false);

    const pull = async () => {
      if (pulling || stopped) return;
      pulling = true;
      const revisionAtStart = socketRevision;
      try {
        const res = await fetch(apiUrl(`/api/public/shooting/${encodeURIComponent(slug)}`), {
          cache: 'no-store',
          credentials: 'omit',
          signal: controller.signal,
        });
        // A slower HTTP response must not overwrite a fresher socket snapshot.
        if (stopped || revisionAtStart !== socketRevision) return;
        if (res.status === 404) {
          stopped = true;
          socket?.close();
          if (pollTimer != null) window.clearInterval(pollTimer);
          setNotFound(true);
          return;
        }
        if (!res.ok) return;
        const body = (await res.json()) as PublicLeaderboard;
        if (!body.ok || stopped || revisionAtStart !== socketRevision) return;
        const signature = boardSignature(body);
        if (signature !== seen) {
          seen = signature;
          setBoard(body);
        }
        setLive(true);
      } catch (err) {
        if ((err as Error).name === 'AbortError') return;
        setLive(false);
      } finally {
        pulling = false;
      }
    };

    const connect = () => {
      const url = scoringWebSocketUrl(apiUrl(`/api/public/shooting/${encodeURIComponent(slug)}/live`));
      socket = new WebSocket(url);
      socket.onmessage = (event) => {
        try {
          const message = JSON.parse(String(event.data)) as { type?: string; leaderboard?: PublicLeaderboard | null };
          if (message.type === 'leaderboard' && message.leaderboard?.ok) {
            socketRevision += 1;
            const signature = boardSignature(message.leaderboard);
            if (signature !== seen) {
              seen = signature;
              setBoard(message.leaderboard);
            }
            setLive(true);
          }
        } catch {
          // Keep the last good board.
        }
      };
      socket.onclose = () => {
        if (stopped) return;
        setLive(false);
        const delay = Math.min(8000, 1000 * 2 ** attempt);
        attempt += 1;
        reconnectTimer = window.setTimeout(connect, delay);
      };
      socket.onopen = () => {
        attempt = 0;
      };
    };

    void pull();
    pollTimer = window.setInterval(() => void pull(), BIG_SCREEN_POLL_MS);
    connect();

    return () => {
      stopped = true;
      controller.abort();
      if (pollTimer != null) window.clearInterval(pollTimer);
      if (reconnectTimer != null) window.clearTimeout(reconnectTimer);
      socket?.close();
    };
  }, [slug]);

  const counts = qualifierCountsFor(board?.bigScreen ?? { qualifiers: BIG_SCREEN_DEFAULT_QUALIFIERS });
  const columns = useMemo(() => bigScreenColumns(board?.rows ?? [], counts), [board, counts]);
  const rifle = (board?.tournament.slug ?? slug) === RIFLE_TOURNAMENT_SLUG;
  const theme = pendingTheme ?? (rifle ? screenTheme(board) : bigScreenThemeOrDark(board?.bigScreen?.theme));
  const boardView: BigScreenBoardView = board?.bigScreen?.round === 2 ? 'round2' : 'round1';
  const queue = loadBigScreenQueue(boardView === 'round2' ? board?.round2Queue : board?.queue);

  async function changeTheme(next: BigScreenTheme) {
    if (!board) return;
    setPendingTheme(next);
    setThemeError(null);
    setThemeSaving(true);
    try {
      await apiFetch(`/api/admin/shooting/tournaments/${board.tournament.id}/big-screen-theme`, {
        method: 'PATCH',
        body: JSON.stringify({ theme: next }),
      });
      setBoard((current) =>
        current?.bigScreen ? { ...current, bigScreen: { ...current.bigScreen, theme: next, storedTheme: next } } : current,
      );
      setPendingTheme(null);
    } catch (err) {
      setPendingTheme(null);
      setThemeError(err instanceof ApiError ? err.message : 'Could not save the theme.');
    } finally {
      setThemeSaving(false);
    }
  }

  async function changeRound(next: BigScreenBoardView) {
    if (!board) return;
    setThemeError(null);
    try {
      const round = next === 'round2' ? 2 : 1;
      await apiFetch(`/api/admin/shooting/tournaments/${board.tournament.id}/big-screen-round`, { method: 'PATCH', body: JSON.stringify({ round }) });
      setBoard((current) => current?.bigScreen ? { ...current, bigScreen: { ...current.bigScreen, round } } : current);
    } catch (err) {
      setThemeError(err instanceof ApiError ? err.message : 'Could not change the round.');
    }
  }

  if (notFound) return <ScoreboardNotFound />;

  return (
    <div className={`bigscreen-viewport ${theme}`}>
      <div className="bigscreen-scale" style={{ transform: `scale(${scale})` }}>
        {board && rifle ? <BigScreenSlideshow
          key={`${board.tournament.id}:${boardView}`}
          board={board}
          round={boardView === 'round2' ? 2 : 1}
          theme={theme}
          isAdmin={isAdmin}
          onRound={isAdmin ? (round) => void changeRound(round === 2 ? 'round2' : 'round1') : undefined}
          onTheme={(next) => void changeTheme(next)}
          themeSaving={themeSaving}
          error={themeError}
          connected={live}
        /> : <BigScreenStage
          theme={theme}
          isAdmin={isAdmin}
          missingGender={board?.bigScreen?.missingGender ?? columns.missingGender}
          themeError={themeError}
          themeSaving={themeSaving}
          onTheme={(next) => void changeTheme(next)}
          men={columns.male}
          women={columns.female}
          menCut={rifle ? 0 : counts.male}
          womenCut={rifle ? 0 : counts.female}
          holdEnd={holdEnd}
          queue={queue}
          clock={clock}
          live={live}
          rifle={rifle}
          boardView={boardView}
          onBoardView={isAdmin ? (next) => void changeRound(next) : undefined}
          round1={board?.round1 ?? null}
          round2Board={board?.round2 ?? null}
        />}
      </div>
    </div>
  );
}
